import {
  Button,
  Card,
  Center,
  Heading,
  Icon,
  Link,
  ModalBody,
  ModalContent,
  ModalHeader,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  type ChainFullId,
  ChainNamespaces,
  parseChainFullId,
  useChainRegistry,
} from "@umami/chains";
import { useDynamicModalContext } from "@umami/components";
import { assetsActions, useAppDispatch, useSelectedNetwork } from "@umami/state";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import {
  AlertTriangleIcon,
  CheckCircleIcon,
  ExternalLinkIcon,
  ViewOperationsIcon,
} from "../../assets/icons";
import { useColor } from "../../styles/useColor";
import { ModalCloseButton } from "../CloseButton";
import { ShareSecretKeyBox } from "../SafeSend";

type SuccessStepProps = {
  chainId?: ChainFullId;
  hash: string;
  isSafeSend?: boolean;
  secret?: { safeSendHash: string; secret: string };
  dAppNotificationError?: string;
};

export const SuccessStep = ({
  chainId,
  hash,
  isSafeSend,
  secret = { safeSendHash: "", secret: "" },
  dAppNotificationError,
}: SuccessStepProps) => {
  const network = useSelectedNetwork();
  const { registry } = useChainRegistry();
  const dispatch = useAppDispatch();
  const hasRequestedRefetch = useRef(false);

  // an operation was just submitted — bump the refetch trigger so balances,
  // tokens and operations refresh immediately instead of waiting for the next poll.
  // an empty hash means nothing was broadcast (WalletConnect typed-data signing, or a
  // failure before submission), and the ref keeps StrictMode's replayed mount effect
  // from firing a second refresh burst for the same operation
  useEffect(() => {
    if (!hash || hasRequestedRefetch.current) {
      return;
    }
    hasRequestedRefetch.current = true;
    dispatch(assetsActions.refetch());
  }, [dispatch, hash]);

  let transactionExplorerUrl;

  if (chainId && parseChainFullId(chainId).namespace !== ChainNamespaces.TEZOS) {
    transactionExplorerUrl = `${registry.getExplorerUrl(chainId)}/tx/${hash}`;
  } else {
    transactionExplorerUrl = `${network.explorerUrl}/${hash}`;
  }

  const isSafeSendOperation = isSafeSend || secret.secret !== "";

  const { onClose } = useDynamicModalContext();
  const navigate = useNavigate();
  const color = useColor();

  type StepData = [React.ElementType, string, string];

  const getIconAndHeader = (): StepData => {
    if (dAppNotificationError) {
      return [AlertTriangleIcon, color("red"), "Operation submitted but dApp not notified"];
    }

    return [CheckCircleIcon, color("green"), "Operation submitted"];
  };

  const Message = () => {
    const successText = isSafeSendOperation
      ? "It may take up to 30 seconds to appear."
      : "You can follow this operation's progress in the Operations section. It may take up to 30 seconds to appear.";

    if (dAppNotificationError) {
      return (
        <>
          <Text marginTop="12px" data-testid="do-not-retry-text" size="md">
            Do <strong>not</strong> retry this operation; it has already been processed. You may
            need to reload the dApp to see the updated status.
          </Text>
          <Text marginTop="12px" data-testid="dapp-noticaition-error" size="md">
            {dAppNotificationError}
          </Text>
          <Text marginTop="12px" data-testid="success-text" size="md">
            {successText}
          </Text>
        </>
      );
    } else {
      return (
        <Text data-testid="success-text" size="md">
          {successText}
        </Text>
      );
    }
  };

  const [icon, iconColor, heading] = getIconAndHeader();

  return (
    <ModalContent paddingY="20px">
      <ModalHeader textAlign="center">
        <ModalCloseButton />
        <Center flexDirection="column">
          <Icon as={icon} boxSize="24px" marginBottom="18px" color={iconColor} />
          <Heading marginTop="24px" size="xl">
            {heading}
          </Heading>
          <Card maxWidth="340px" color={color("700")} fontWeight="400" whiteSpace="pre-line">
            <Message />
          </Card>
        </Center>
      </ModalHeader>
      {isSafeSendOperation && (
        <ModalBody>
          <VStack width="100%" spacing="24px">
            <ShareSecretKeyBox safeSendHash={secret.safeSendHash} secret={secret.secret} />

            <Button
              as={Link}
              gap="10px"
              width="full"
              isExternal
              onClick={() => {
                onClose();
                navigate("/activity");
              }}
              variant="ghost"
            >
              <ViewOperationsIcon />
              <Text marginRight="4px" marginLeft="4px">
                See all operations
              </Text>
            </Button>
          </VStack>
        </ModalBody>
      )}
      {!isSafeSendOperation && (
        <ModalBody gap="24px">
          <Button
            width="100%"
            onClick={() => {
              onClose();
              navigate("/activity");
            }}
            size="lg"
            variant="primary"
          >
            See all Operations
          </Button>
          <Button
            as={Link}
            gap="10px"
            width="full"
            href={transactionExplorerUrl}
            isExternal
            variant="ghost"
          >
            <ExternalLinkIcon color={color("400")} />
            <Text marginRight="4px">View in Explorer</Text>
          </Button>
        </ModalBody>
      )}
    </ModalContent>
  );
};
